import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { Parse } from "../lib/parse";

const normalizeDocumentPayload = (response) => {
  if (!response) return null;
  if (response?.error) return null;
  if (response?.result?.error) return null;
  return response?.result || response;
};

const toPlainDocument = (value) => {
  if (!value) return null;
  if (typeof value.toJSON === "function") {
    return value.toJSON();
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const fetchDocumentByCloud = async (docId) => {
  try {
    const response = await Parse.Cloud.run("getDocument", { docId });
    return normalizeDocumentPayload(response);
  } catch (error) {
    return null;
  }
};

const fetchDocumentByQuery = async (docId) => {
  try {
    const query = new Parse.Query("contracts_Document");
    query.equalTo("objectId", docId);
    query.notEqualTo("IsArchive", true);
    return toPlainDocument(await query.first());
  } catch (error) {
    return null;
  }
};

const secureUrl = async (rawUrl, docId = "") => {
  if (!rawUrl) return "";
  try {
    const signedUrl = await Parse.Cloud.run("getsignedurl", {
      url: rawUrl,
      ...(docId ? { docId } : {})
    });
    if (typeof signedUrl === "string" && signedUrl.trim()) {
      return signedUrl;
    }
  } catch (error) {
    // fallback to raw url
  }
  return rawUrl;
};

const triggerDownload = (url) => {
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

function SignedDocumentSummaryPage() {
  const { t } = useTranslation();
  const { docId = "" } = useParams();
  const location = useLocation();
  const stateSnapshot = location.state || {};
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingCertificate, setIsGeneratingCertificate] = useState(false);
  const [error, setError] = useState("");
  const [docSummary, setDocSummary] = useState({
    name: String(stateSnapshot?.name || ""),
    signedUrl: String(stateSnapshot?.signedUrl || ""),
    certificateUrl: String(stateSnapshot?.certificateUrl || ""),
    isCompleted: true,
    signers: stateSnapshot?.signers || [],
    completedDate: stateSnapshot?.completedDate || null
  });

  const refreshDocument = async () => {
    if (!docId) {
      setIsLoading(false);
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      const cloudDoc = await fetchDocumentByCloud(docId);
      const queryDoc = cloudDoc || (await fetchDocumentByQuery(docId));
      if (!queryDoc) {
        throw new Error(
          t("new-ui.individual-signing.document-not-found", {
            defaultValue: "Document not found or access denied."
          })
        );
      }
      const resolvedSignedUrl = await secureUrl(queryDoc?.SignedUrl || queryDoc?.URL || "", docId);
      const resolvedCertificateUrl = await secureUrl(queryDoc?.CertificateUrl || "", docId);
      
      // Extract completion date from audit trail
      const auditTrail = queryDoc?.AuditTrail || [];
      const signedEntries = auditTrail.filter(x => x.Activity === 'Signed');
      const lastSignedEntry = signedEntries[signedEntries.length - 1];
      const completedDate = lastSignedEntry?.SignedOn || lastSignedEntry?.Date || queryDoc?.updatedAt;
      
      setDocSummary({
        name:
          queryDoc?.Name ||
          docSummary?.name ||
          t("document-title", { defaultValue: "Document" }),
        signedUrl: resolvedSignedUrl,
        certificateUrl: resolvedCertificateUrl,
        isCompleted: Boolean(queryDoc?.IsCompleted),
        signers: queryDoc?.Signers || [],
        completedDate: completedDate
      });
    } catch (loadError) {
      setError(
        loadError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const onGenerateCertificate = async () => {
    if (!docId) return;
    setError("");
    setIsGeneratingCertificate(true);
    try {
      const response = await Parse.Cloud.run("generatecertificate", { docId });
      const nextCertificateUrl = await secureUrl(response?.CertificateUrl || "", docId);
      if (!nextCertificateUrl) {
        throw new Error(
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
        );
      }
      setDocSummary((current) => ({
        ...current,
        certificateUrl: nextCertificateUrl
      }));
    } catch (certificateError) {
      setError(
        certificateError?.message ||
          t("generate-certificate-err", {
            defaultValue:
              "We are currently experiencing an issue with certificate generation. Please try again later."
          })
      );
    } finally {
      setIsGeneratingCertificate(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "";
    }
  };

  return (
    <AppShell>
      <div className="dana-section-grid">
        <Card className="animate-fade-up [animation-delay:40ms]">
          <p className="ui-kicker">
            {t("new-ui.individual-signing.completed-kicker", {
              defaultValue: "Signing completed"
            })}
          </p>
          <h1 className="ui-title mt-2 text-xl">
            {t("new-ui.individual-signing.completed-title", {
              defaultValue: "Document finalized"
            })}
          </h1>
          <p className="ui-copy mt-2">
            {t("new-ui.individual-signing.completed-description", {
              defaultValue:
                "This document is finalized. You can download the signed PDF and the completion certificate from this page."
            })}
          </p>
          
          {docSummary?.name && (
            <div className="mt-4 space-y-2 text-sm">
              <p className="text-app-ink">
                <span className="font-semibold">
                  {t("document-title", { defaultValue: "Document" })}:
                </span>{" "}
                {docSummary.name}
              </p>
              
              {docSummary.completedDate && (
                <p className="text-app-ink">
                  <span className="font-semibold">
                    {t("document-completed", { defaultValue: "Completed on" })}:
                  </span>{" "}
                  {formatDate(docSummary.completedDate)}
                </p>
              )}
              
              {docSummary.signers && docSummary.signers.length > 0 && (
                <div className="text-app-ink">
                  <p className="font-semibold mb-1">
                    {t("signed-by", { defaultValue: "Signed by" })}:
                  </p>
                  <ul className="list-disc list-inside pl-2 space-y-1">
                    {docSummary.signers.map((signer, index) => (
                      <li key={index}>
                        {signer.Name} ({signer.Email})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {error ? <p className="mt-3 text-sm text-app-danger">{error}</p> : null}

          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => triggerDownload(docSummary?.signedUrl)}
              disabled={isLoading || !docSummary?.signedUrl}
            >
              {t("new-ui.individual-signing.download-signed", {
                defaultValue: "Download signed document"
              })}
            </Button>

            {docSummary?.certificateUrl ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => triggerDownload(docSummary?.certificateUrl)}
                disabled={isLoading}
              >
                {t("new-ui.individual-signing.download-certificate", {
                  defaultValue: "Download certificate"
                })}
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={onGenerateCertificate}
                disabled={isLoading || isGeneratingCertificate}
              >
                {isGeneratingCertificate
                  ? t("generating-certificate", { defaultValue: "Generating certificate" })
                  : t("new-ui.individual-signing.generate-certificate", {
                      defaultValue: "Generate certificate"
                    })}
              </Button>
            )}

            <Link to="/form/sHAnZphf69">
              <Button type="button" variant="ghost">
                {t("new-ui.individual-signing.back-sign-yourself", {
                  defaultValue: "Back to sign yourself"
                })}
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

export default SignedDocumentSummaryPage;
